// client/src/components/pages/LexiStar/Replay/idleHeatmapLogic.ts
//
// Idling heatmap logic (ST-00188 M4-D; LS-03 FR-RPL-007; LS-09 §5.6; m4-contracts
// §3 `telemetry.idle_cells_day` and §4 GET /api/lexistar/idle-heatmap).
//
// The server aggregates (LS-09 §6 "heatmaps aggregated server-side") into a
// 0.002° grid (≈ 200 m); the page only draws the cells it is given and ranks
// nothing itself — the hotspot table is the API's ranking. Cells are keyed by
// their south-west corner (floor(lat / size) · size), the same convention as
// the continuous aggregate; a cell that arrives as a centre is snapped to its
// corner so both conventions draw the same square.
//
// Colour: one sequential ramp (magnitude → light to dark) in the idle hue of
// the LS-09 §4 status language (idling = yellow), so "idle" reads the same on
// the heatmap as on the status board — and no other state colour appears on
// this map.

import type { IdleCell, IdleHeatmapResponse, IdleHotspot } from '../../../../types/lexistar-analytics';

export const CELL_DEG = 0.002;

/** Five steps, light → dark (Tailwind yellow-200 → amber-800); lightness is monotone. */
export const HEAT_RAMP = ['#fef08a', '#fcd34d', '#f59e0b', '#d97706', '#92400e'] as const;
export const HEAT_OUTLINE = '#78350f';

export interface HeatCell {
  id: string;
  /** South-west corner. */
  lat: number;
  lon: number;
  size: number;
  idle_h: number;
  visits: number;
  assets: number;
  /** Idle inside a permitted zone (source, destination, weighbridge, delay, …). */
  permitted: boolean;
  zone_name: string | null;
}

export interface Hotspot {
  id: string;
  rank: number;
  /** Cell centre — where the map centres. */
  lat: number;
  lon: number;
  cell_id: string;
  label: string;
  idle_h: number;
  visits: number;
  est_litres: number | null;
  est_rand: number | null;
  permitted: boolean;
  zone_name: string | null;
  assets: number;
  asset_keys: string[];
}

export interface HeatmapSummary {
  idle_h: number;
  permitted_idle_h: number;
  elsewhere_idle_h: number;
  est_litres: number | null;
  est_rand: number | null;
  cells: number;
  idle_lph: number | null;
  fuel_price: number | null;
  exclude_permitted: boolean;
}

export interface Heatmap {
  cells: HeatCell[];
  hotspots: Hotspot[];
  summary: HeatmapSummary;
}

const num = (v: unknown): number | null => {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

/** Floor to the grid (with a nudge so 0.004 / 0.002 does not land in cell 1.9999). */
export const snap = (deg: number, size: number = CELL_DEG): number =>
  Number((Math.floor(deg / size + 1e-9) * size).toFixed(6));

export const cellId = (lat: number, lon: number): string => `${lat.toFixed(4)}:${lon.toFixed(4)}`;

/** The SW corner of an API cell: its cell_lat / cell_lon, else the centre snapped to the grid. */
const cornerOf = (c: Pick<IdleCell, 'lat' | 'lon' | 'cell_lat' | 'cell_lon'>, size: number): [number, number] => {
  const lat = num(c.cell_lat);
  const lon = num(c.cell_lon);
  if (lat !== null && lon !== null) return [snap(lat, size), snap(lon, size)];
  return [snap(Number(c.lat), size), snap(Number(c.lon), size)];
};

export function normaliseCell(c: IdleCell, size: number = CELL_DEG): HeatCell | null {
  const [lat, lon] = cornerOf(c, size);
  if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null;
  return {
    id: cellId(lat, lon),
    lat,
    lon,
    size,
    idle_h: num(c.idle_h) ?? 0,
    visits: num(c.visits) ?? 0,
    assets: num(c.assets) ?? 0,
    permitted: Boolean(c.permitted),
    zone_name: c.zone?.name ?? null,
  };
}

export function normaliseHotspot(h: IdleHotspot, i: number, size: number = CELL_DEG): Hotspot | null {
  const [cLat, cLon] = cornerOf(h, size);
  const lat = num(h.lat) ?? cLat + size / 2;
  const lon = num(h.lon) ?? cLon + size / 2;
  if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null;
  const zone = h.zone?.name ?? null;
  return {
    id: `${cellId(cLat, cLon)}#${num(h.rank) ?? i + 1}`,
    rank: num(h.rank) ?? i + 1,
    lat,
    lon,
    cell_id: cellId(cLat, cLon),
    label: zone ?? `${lat.toFixed(4)}, ${lon.toFixed(4)}`,
    idle_h: num(h.idle_h) ?? 0,
    visits: num(h.visits) ?? 0,
    est_litres: num(h.est_litres),
    est_rand: num(h.est_rand),
    permitted: Boolean(h.permitted),
    zone_name: zone,
    assets: num(h.assets) ?? (h.asset_keys || []).length,
    asset_keys: (h.asset_keys || []).filter(Boolean),
  };
}

/** GET /idle-heatmap body → Heatmap. */
export function normaliseHeatmap(body: IdleHeatmapResponse | null | undefined): Heatmap {
  const size = num(body?.cell_deg) || CELL_DEG;
  const cells = (body?.cells || []).map((c) => normaliseCell(c, size)).filter((c): c is HeatCell => c !== null);
  const hotspots = (body?.hotspots || [])
    .map((h, i) => normaliseHotspot(h, i, size))
    .filter((h): h is Hotspot => h !== null)
    .sort((a, b) => a.rank - b.rank);
  const t = body?.totals;
  return {
    cells,
    hotspots,
    summary: {
      idle_h: num(t?.idle_h) ?? cells.reduce((a, c) => a + c.idle_h, 0),
      permitted_idle_h: num(t?.permitted_idle_h) ?? 0,
      elsewhere_idle_h: num(t?.elsewhere_idle_h) ?? 0,
      est_litres: num(t?.est_litres),
      est_rand: num(t?.est_rand),
      cells: cells.length,
      idle_lph: num(body?.norms?.idle_lph),
      fuel_price: num(body?.fuel_price?.price_per_l),
      exclude_permitted: Boolean(body?.exclude_permitted),
    },
  };
}

/** A "nice" number for a legend break: 1, 2, 2.5, 5 × 10ⁿ, never below 0.1 h. */
export function niceBreak(v: number): number {
  if (!(v > 0)) return 0.1;
  const e = Math.floor(Math.log10(v));
  const base = 10 ** e;
  const f = v / base;
  const nice = f < 1.5 ? 1 : f < 2.25 ? 2 : f < 3.5 ? 2.5 : f < 7.5 ? 5 : 10;
  return Math.max(0.1, Number((nice * base).toFixed(2)));
}

/**
 * Four upper-bound breaks for five buckets from the cells' idle hours
 * (quantiles, so a few depot cells with hundreds of hours do not wash every
 * other cell into the palest step). Strictly increasing.
 */
export function heatBreaks(hours: number[]): number[] {
  const v = hours.filter((h) => h > 0).sort((a, b) => a - b);
  if (!v.length) return [0.5, 1, 2, 5];
  const q = (p: number) => v[Math.min(v.length - 1, Math.floor(p * (v.length - 1)))] ?? 0;
  const raw = [q(0.4), q(0.7), q(0.88), q(0.97)].map(niceBreak);
  const out: number[] = [];
  raw.forEach((b) => {
    const prev = out[out.length - 1];
    out.push(prev !== undefined && b <= prev ? niceBreak(prev * 2.1) : b);
  });
  return out;
}

export const bucketOf = (h: number, breaks: number[]): number => {
  const i = breaks.findIndex((b) => h <= b);
  return i === -1 ? breaks.length : i;
};

export const heatColour = (h: number, breaks: number[]): string =>
  HEAT_RAMP[Math.min(HEAT_RAMP.length - 1, bucketOf(h, breaks))] ?? HEAT_RAMP[0];

const fmtH = (h: number): string => (h < 10 ? h.toFixed(1).replace(/\.0$/, '') : String(Math.round(h)));

/** Legend labels per bucket: "≤ 0.5 h", "0.5–1 h", …, "> 5 h". */
export function legendLabels(breaks: number[]): string[] {
  return HEAT_RAMP.map((_, i) => {
    if (i === 0) return `≤ ${fmtH(breaks[0] ?? 0)} h`;
    if (i >= breaks.length) return `> ${fmtH(breaks[breaks.length - 1] ?? 0)} h`;
    return `${fmtH(breaks[i - 1] ?? 0)}–${fmtH(breaks[i] ?? 0)} h`;
  });
}

type Position = [number, number];

export interface CellFeature {
  type: 'Feature';
  id: number;
  properties: { id: string; idle_h: number; visits: number; colour: string; permitted: boolean };
  geometry: { type: 'Polygon'; coordinates: Position[][] };
}

export const cellRing = (c: Pick<HeatCell, 'lat' | 'lon' | 'size'>): Position[] => [
  [c.lon, c.lat],
  [c.lon + c.size, c.lat],
  [c.lon + c.size, c.lat + c.size],
  [c.lon, c.lat + c.size],
  [c.lon, c.lat],
];

/** Cells → a GeoJSON FeatureCollection with the bucket colour baked in (the layer reads ['get', 'colour']). */
export function cellFeatures(cells: HeatCell[], breaks: number[]) {
  return {
    type: 'FeatureCollection' as const,
    features: cells.map(
      (c, i): CellFeature => ({
        type: 'Feature',
        id: i,
        properties: {
          id: c.id,
          idle_h: c.idle_h,
          visits: c.visits,
          colour: heatColour(c.idle_h, breaks),
          permitted: c.permitted,
        },
        geometry: { type: 'Polygon', coordinates: [cellRing(c)] },
      })
    ),
  };
}

export interface CellPointFeature {
  type: 'Feature';
  id: number;
  properties: { id: string; idle_h: number; visits: number; colour: string };
  geometry: { type: 'Point'; coordinates: Position };
}

/**
 * The same cells as points at their centres, for the zoomed-out view: a 200 m
 * square is a speck at province zoom, so below street zoom each cell draws as
 * a small disc in its bucket colour (darkest on top) and the true squares take
 * over as the map zooms in.
 */
export function cellPoints(cells: HeatCell[], breaks: number[]) {
  return {
    type: 'FeatureCollection' as const,
    features: cells.map(
      (c, i): CellPointFeature => ({
        type: 'Feature',
        id: i,
        properties: { id: c.id, idle_h: c.idle_h, visits: c.visits, colour: heatColour(c.idle_h, breaks) },
        geometry: { type: 'Point', coordinates: [c.lon + c.size / 2, c.lat + c.size / 2] },
      })
    ),
  };
}

/** Zoom at which the true cell squares replace the discs. */
export const SQUARES_FROM_ZOOM = 11;

/** [[west, south], [east, north]] around every cell, or null. */
export function cellsBounds(cells: HeatCell[]): [Position, Position] | null {
  if (!cells.length) return null;
  let w = Infinity;
  let s = Infinity;
  let e = -Infinity;
  let n = -Infinity;
  cells.forEach((c) => {
    w = Math.min(w, c.lon);
    s = Math.min(s, c.lat);
    e = Math.max(e, c.lon + c.size);
    n = Math.max(n, c.lat + c.size);
  });
  return [
    [w, s],
    [e, n],
  ];
}

/** Share of the estimate that is fuel — the rand figure is always an estimate (LS-04 §11). */
export function estimateNote(summary: HeatmapSummary): string {
  const parts: string[] = [];
  if (summary.idle_lph !== null) parts.push(`${summary.idle_lph.toLocaleString('en-GB', { maximumFractionDigits: 1 })} L/h idle norm`);
  if (summary.fuel_price !== null) parts.push(`R${summary.fuel_price.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}/L`);
  return parts.length
    ? `Estimated: idle hours × ${parts.join(' × ')}.`
    : 'Estimated: idle hours × the idle fuel norm × the fuel price.';
}
